// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  type Action as ReduxAction,
  configureStore,
  type Dispatch,
  type Middleware,
  type Reducer,
  type StoreEnhancer,
  type UnknownAction,
  type DevToolsEnhancerOptions,
  type ConfigureStoreOptions,
  type EnhancedStore,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
  useStore,
} from 'react-redux';
import { deepEqual as isEqual } from 'fast-equals';

export type GetDefaultMiddlewareOptions = Parameters<
  Parameters<Required<ConfigureStoreOptions>['middleware']>[0]
>[0];

export type GetDefaultEnhancersOptions = Parameters<
  Parameters<Required<ConfigureStoreOptions>['enhancers']>[0]
>[0];

/**
 * The configuration options for the {@link setupStore} function.
 *
 * @interface
 */
export interface SetupStoreOptions<
  State extends {},
  Action extends ReduxAction = UnknownAction,
> {
  /** The Redux reducer function. */
  readonly reducer: Reducer<State, Action>;

  /**
   * The preloaded state of the Redux store.
   *
   * If not set, the store will be initialized with an empty state.
   */
  readonly preloadedState?: State;

  /**
   * Whether to set up the @reduxjs/toolkit/query listeners.
   *
   * @default true
   */
  readonly setupStoreListeners?: boolean;

  /** The additional enhancers to be added. */
  readonly enhancers?: StoreEnhancer<any, any>[];

  /** The additional middleware functions to be added. */
  readonly middlewares?: Middleware<any, any, Dispatch<UnknownAction>>[];

  /**
   * The configuration options for the middleware.
   *
   * See the
   * {@link https://redux-toolkit.js.org/api/getDefaultMiddleware#intended-usage}
   * function for more information.
   */
  readonly middlewareOptions?: GetDefaultMiddlewareOptions;

  /** The configuration options for the enhancers. */
  readonly enhancersOptions?: GetDefaultEnhancersOptions;

  /**
   * The configuration options for the Redux DevTools.
   *
   * If set to `true`, the DevTools will be enabled with the default options. If set
   * to `false`, the DevTools will not be enabled. If set to an object, the DevTools
   * will be enabled with the specified options.
   *
   * @default { trace: true }
   */
  readonly devTools?: boolean | DevToolsEnhancerOptions;
}

/**
 * Creates a Redux store with a given reducer, preloaded state, and configuration
 * options.
 *
 * @param reducer - The Redux reducer function.
 * @param preloadedState - The preloaded state of the Redux store.
 * @param setupStoreListeners - Set up the @reduxjs/toolkit/query listeners.
 * @param middlewares - The additional middleware functions to be added.
 * @param enhancers - The additional enhancers to be added.
 * @param devTools - The configuration options for the Redux DevTools.
 * @param middlewareOptions - The configuration options for the middleware.
 * @param enhancersOptions - The configuration options for the enhancers.
 * @returns The created Redux store.
 */
export const setupStore = <
  State extends {},
  Action extends ReduxAction = UnknownAction,
>({
  reducer,
  preloadedState,
  setupStoreListeners,
  middlewares = [],
  enhancers = [],
  devTools = { trace: true },
  middlewareOptions,
  enhancersOptions,
}: SetupStoreOptions<State, Action>) => {
  const store = configureStore({
    reducer: reducer,
    devTools: devTools,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware(middlewareOptions).concat(middlewares),
    enhancers: getDefaultEnhancers =>
      getDefaultEnhancers(enhancersOptions).concat(enhancers),
    preloadedState: preloadedState,
  });

  // istanbul ignore if
  if (setupStoreListeners) setupListeners(store.dispatch);

  return store;
};

/**
 * Creates a set of custom React hooks to interact with the Redux store.
 *
 * This function takes a Redux store as an argument and returns an object containing
 * three custom hooks: `useAppDispatch`, `useAppSelector`, and `useAppStore`.
 *
 * @param store - The Redux store.
 * @returns An object containing the custom hooks.
 */
export const createReduxHooks = <T extends EnhancedStore>() => {
  type AppDispatch = T['dispatch'];

  type AppState = ReturnType<T['getState']>;

  /**
   * Custom hook to use the Redux store's dispatch function.
   *
   * @returns The dispatch function.
   */
  const useAppDispatch = (): AppDispatch => useDispatch();

  /**
   * Custom hook to use the Redux store's state.
   *
   * @param selector - The selector function to extract data from the state.
   * @param equalityFn - The function to determine if the selected data has changed.
   * @returns The selected data from the state.
   */
  /* istanbul ignore next */
  const useAppSelector: TypedUseSelectorHook<AppState> = (
    selector,
    equalityFn = isEqual,
  ) => useSelector(selector, equalityFn);

  /**
   * Custom hook to use the Redux store.
   *
   * @returns The Redux store.
   */
  const useAppStore = () => useStore<T>();

  return {
    useAppDispatch,
    useAppSelector,
    useAppStore,
  };
};
